import { useIntl } from "react-intl";
import { RPFormattedMessage } from "@src/i18n";
import {
  LicenseStatus,
  OrgCountry,
  useOrderInfo,
  useOrgId,
  useOrgInfo,
  useUserInfo,
} from "@src/common/reliablePartnerApi";
import ErrorPage from "@src/ErrorPage";
import { useAppSelector } from "@src/reduxStore";
import LK2Info from "@src/LK2Info";

const useOrderPage = () => {
  const orgInfo = useOrgInfo();
  const orderInfo = useOrderInfo();
  const userInfo = useUserInfo();

  let status: "error" | "idle" | "loading" | "success" = "idle";
  if (orgInfo.isSuccess && orderInfo.isSuccess) {
    status = "success";
    // We have separate return statement here
    // to make typescript understand on the caller
    // that when the status is "success",
    // all queries are of type QueryObserverSuccessResult.
    return { status, orgInfo, orderInfo, userInfo };
  } else if (orgInfo.isError || orderInfo.isError) {
    status = "error";
  } else if (orgInfo.isLoading || orderInfo.isLoading) {
    status = "loading";
  }
  return { status, orgInfo, orderInfo, userInfo };
};

const OrderPage = () => {
  const intl = useIntl();
  const orgId = useOrgId();
  const orderPageQuery = useOrderPage();
  const config = useAppSelector(state => state.common.config);

  // Navigate the browser to the join endpoint, which issues a 302 redirect to
  // the external ordering form. This must be a top-level navigation: a fetch()
  // cannot follow a cross-origin redirect and read the response (Same-Origin
  // Policy), which caused a CORS error and bounced the user to the landing page.
  const handleJoinOrder = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    e.preventDefault();
    window.location.href = `/api/orgs/${orgId}/join`;
  };

  switch (orderPageQuery.status) {
    case "idle":
    case "loading":
      return <div className="page-loading-indicator" />;
    case "error":
      return (
        <ErrorPage>
          <RPFormattedMessage
            id="loadingLicenseError"
            defaultMessage="Tilaustietojen haku epäonnistui. Kokeile hetken päästä uudelleen."
          />
        </ErrorPage>
      );
    case "success":
      const { orderInfo, orgInfo, userInfo } = orderPageQuery;
      const license = orderInfo.data.license;
      return (
        <div className="d-flex flex-row justify-content-center mt-5">
          <div className="col-md-10">
            <h1>
              <RPFormattedMessage
                id="orderPageHeader"
                defaultMessage="Luotettava Kumppani tilaajavastuutiedot -palveluun liittyminen"
              />
            </h1>
            <LK2Info />
            {license?.status === LicenseStatus.ACTIVE && (
              <UserPermissionInfo />
            )}
            <div>
              {license === null && (
                <>
                  <p className="mb-6">
                    <RPFormattedMessage
                      id="orderPageNotOrdered"
                      defaultMessage="Yritystäsi <b>{orgName}</b> ei ole vielä liitetty Luotettava Kumppani tilaajavastuutiedot -palveluun."
                    />
                  </p>
                  {orgInfo.data?.role === "main" ||
                  orgInfo.data?.role === "basic" ||
                  userInfo.data?.isAdmin ? (
                    <>
                      {orgInfo.data?.country === OrgCountry.FI ? (
                        <>
                          <p className="mb-6">
                            <RPFormattedMessage
                              id="pricingInfo"
                              defaultMessage="Huomaathan, että palvelu on maksullinen. Tarkista vuosimaksu <linkToPricelist>hinnastostamme</linkToPricelist>."
                            />
                          </p>
                          <button
                            className="btn btn-primary"
                            onClick={handleJoinOrder}
                          >
                            <RPFormattedMessage
                              id="orderPageOrderButtonLabelFinnishOrg"
                              defaultMessage="Siirry tilaamaan"
                            />
                          </button>
                        </>
                      ) : (
                        <>
                          <p>
                            <RPFormattedMessage
                              id="orderPageOrderInfoForeignOrg"
                              defaultMessage="<b>Liittyminen</b> Luotettava Kumppani tilaajavastuutiedot -palveluun <b>edellyttää valtakirjan täyttämistä</b>: valtakirjalla valtuutat Vastuu Groupin hakemaan ja päivittämään kaikki tilaajavastuulain vaatimat tiedot yrityksesi puolesta."
                            />
                          </p>
                          <p>
                            <a
                              className="btn btn-primary"
                              href={intl.formatMessage(
                                {
                                  id: "orderPageOrderButtonUrlForeignOrg",
                                  defaultMessage:
                                    "{vgUrl}/fi-fi/palvelut/luotettava-kumppani/pdf-valtakirjat",
                                },
                                {
                                  vgUrl: config?.vgUrl,
                                }
                              )}
                              rel="noreferrer noopener"
                              target="_blank"
                            >
                              <RPFormattedMessage
                                id="orderPageOrderButtonLabelForeignOrg"
                                defaultMessage="Siirry täyttämään valtakirja"
                              />
                            </a>
                            <a
                              className="ml-4"
                              href={intl.formatMessage(
                                {
                                  id: "orderPageOrderReadMoreLinkUrlForeignOrg",
                                  defaultMessage:
                                    "{vgUrl}/fi-fi/palvelut/luotettava-kumppani",
                                },
                                {
                                  vgUrl: config?.vgUrl,
                                }
                              )}
                              rel="noreferrer noopener"
                              target="_blank"
                            >
                              <RPFormattedMessage
                                id="orderPageOrderReadMoreLinkLabelForeignOrg"
                                defaultMessage="tai lue lisää"
                              />
                            </a>
                          </p>
                          <div className="alert alert-info mt-6" role="alert">
                            <i className="fa fa-info-circle fa-mr" />
                            <RPFormattedMessage
                              id="orderPageForeignOrgReminder"
                              defaultMessage={
                                "<b>Tärkeää! Jos yritykselläsi on myös suomalainen y-tunnus</b> (yritysmuotona Suomen sivuliike tai ulkomainen yhteisö), sinun tulee täyttää erilliset valtakirjat sekä suomalaiselle että ulkomaiselle yritykselle."
                              }
                            />
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <RPFormattedMessage
                      id="orderPageOrderInfoNotMainUser"
                      defaultMessage="<p>Vain yrityksen pääkäyttäjä voi tilata palvelun. Ole hyvä ja ota yhteys yrityksen pääkäyttäjään.</p>"
                    />
                  )}
                </>
              )}
              {license?.status === LicenseStatus.ACTIVE && (
                <>
                  <RPFormattedMessage
                    id="orderPageServiceActive"
                    defaultMessage="<p>Yrityksesi <b>{orgName}</b> on ottanut palvelun käyttöön.</p>"
                  />
                  {(orgInfo.data?.role === "main" ||
                    orgInfo.data?.role === "basic" ||
                    userInfo.data?.isAdmin) && (
                    <RPFormattedMessage
                      id="orderPageEndServiceCTA"
                      defaultMessage="Jos haluat irtisanoa Luotettava Kumppani tilaajavastuutiedot -palvelun, ota yhteyttä <linkToCustomerService>asiakaspalveluun</linkToCustomerService>."
                    />
                  )}
                </>
              )}
            </div>
            <div className="mt-6">
              <RPFormattedMessage
                id="tos"
                defaultMessage="<linkToToS>Luotettava Kumppani -palvelun sopimusehdot</linkToToS>"
              />
            </div>
          </div>
        </div>
      );
  }
};

export default OrderPage;

const UserPermissionInfo = () => {
  const orgId = useOrgId();
  const userInfo = useUserInfo();

  if (!orgId || !userInfo.isSuccess) {
    return null;
  }

  const company = userInfo.data.companies.find(
    company => company.qvarnId === orgId
  );
  const canAccess =
    userInfo.data.isAdmin || company?.permissions.includes("rp_user");

  if (!canAccess) {
    return (
      <div className="alert alert-warning">
        <i
          className="fa fa-info-circle mr-4 align-self-center"
          aria-hidden="true"
        ></i>
        <RPFormattedMessage
          id="permissionPageNoPermissionsRepresentedOrg"
          defaultMessage="Sinulta puuttuu käyttöoikeus Luotettava Kumppani -palveluun yritykselle <b>{orgName}</b>. Pyydä tarpeen vaatiessa käyttöoikeus yrityksenne pääkäyttäjältä. <linkToCompanyAccount>Siirry yritystilille</linkToCompanyAccount>."
        />
      </div>
    );
  }

  return null;
};
